import { inject } from '@angular/core';
import { ApolloLink, concat, InMemoryCache } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { HttpLink } from 'apollo-angular/http';
import { OperationDefinitionNode } from 'graphql';

const stripDetailFromTypename = (obj: any, count: number = 0) => {
	count++;
	if (count > 99) {
		return obj;
	}
	Object.keys(obj).forEach(key => {
		if (obj[key]?.__typename?.endsWith('Detail')) {
			obj[key].__typename = obj[key].__typename.substring(
				0,
				obj[key].__typename.indexOf('Detail')
			);
		}

		// Everything is an object in JS, we need to be tacit with our check
		if (
			obj[key] &&
			typeof obj[key] !== 'string' &&
			typeof obj[key] !== 'number' &&
			Object.keys(obj[key]).length > 0
		) {
			obj[key] = stripDetailFromTypename(obj[key], count);
		}
	});
	return obj;
};

const removeTimezonesFromDates = (item: any): any => {
	if (!item) {
		return item;
	}

	let newItem = item;

	if (Object.prototype.toString.call(item) === '[object Date]') {
		const monthsArray = [
			'01',
			'02',
			'03',
			'04',
			'05',
			'06',
			'07',
			'08',
			'09',
			'10',
			'11',
			'12',
		];
		const formattedDate =
			item.getDate().toString().length < 2 ? `0${item.getDate()}` : item.getDate();
		const formattedHours =
			item.getHours().toString().length < 2 ? `0${item.getHours()}` : item.getHours();
		const formattedMinutes =
			item.getMinutes().toString().length < 2 ? `0${item.getMinutes()}` : item.getMinutes();
		const formattedSeconds =
			item.getSeconds().toString().length < 2 ? `0${item.getSeconds()}` : item.getSeconds();
		const formattedMilliseconds =
			item.getMilliseconds().toString().length === 2
				? `0${item.getMilliseconds()}`
				: item.getMilliseconds().toString().length === 1
					? `00${item.getMilliseconds()}`
					: item.getMilliseconds();

		newItem = `${item.getFullYear()}-${
			monthsArray[item.getMonth()]
		}-${formattedDate}T${formattedHours}:${formattedMinutes}:${formattedSeconds}.${formattedMilliseconds}`;
	}
	if (typeof item === 'object' && Object.prototype.toString.call(item) !== '[object Date]') {
		newItem = {};
		Object.keys(item).forEach(key => {
			newItem[key] = removeTimezonesFromDates(item[key]);
		});
	}
	if (Array.isArray(item) && Object.prototype.toString.call(item) !== '[object Date]') {
		newItem = [];
		Object.keys(item).forEach(key => {
			newItem.push(removeTimezonesFromDates(item[key]));
		});
	}
	return newItem;
};

const removeTimezoneMiddleware = new ApolloLink((operation, forward) => {
	// Remove the timezone from dates in mutations
	if (
		(Object.keys(operation.variables) || []).length &&
		operation.query.definitions.some(
			def =>
				def.kind === 'OperationDefinition' &&
				(def as OperationDefinitionNode).operation === 'mutation'
		)
	) {
		operation.variables = removeTimezonesFromDates(operation.variables);
	}

	return forward(operation);
});
const typenameMiddleware = new ApolloLink((operation, forward) => {
	// Remove the 'Detail' from typenames
	return forward(operation).map(ctx => {
		if (ctx.data) {
			ctx.data = stripDetailFromTypename(ctx.data);
		}
		return ctx;
	});
});

export interface ApolloErrorEventHandler {
	apolloLoad(): void;
	apolloUnload(): void;
	apolloOnError(error: any): void;
}

export const apolloProvider =
	(errorEventHandler: ApolloErrorEventHandler, uri: string, cache: InMemoryCache) => () => {
		const httpLink = inject(HttpLink);
		const errorAfterware = onError(({ operation, response, graphQLErrors, networkError }) => {
			if (graphQLErrors) {
				errorEventHandler.apolloOnError({ operation, response, graphQLErrors });
			}
			if (networkError) {
				errorEventHandler.apolloOnError({ operation, response, networkError });
			}
		});

		const link = concat(
			errorAfterware.concat(ApolloLink.from([removeTimezoneMiddleware, typenameMiddleware])),
			httpLink.create({ uri, withCredentials: true })
		);

		return {
			cache,
			link,
		};
	};
